"use client";

import React from "react";
import { motion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, PhoneCall, ArrowRight, ShieldCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
const faqs = [{
  id: "fleet-coverage",
  question: "Do BMF Scaffolding cover Fleet?",
  answer: "Yes. We regularly supply, erect, and dismantle scaffolding across Fleet and the surrounding Hampshire and Surrey borders, including Church Crookham, Elvetham Heath, and nearby commercial zones."
}, {
  id: "scaffolding-types",
  question: "What types of scaffolding can you provide in Fleet?",
  answer: "We provide independent access scaffolding, birdcage scaffolds, chimney access, temporary roofs, edge protection systems, loading bays, and bespoke designed structures for residential, commercial, and industrial sites."
}, {
  id: "roof-work",
  question: "Can you provide scaffolding for roof work?",
  answer: "Yes. We erect scaffolding tailored for pitched and flat roof replacements, re-pointing, chimney rebuilds, solar panel installations, and fully enclosed temporary weather protection roofs."
}, {
  id: "contractors-developers",
  question: "Do you work with contractors and developers?",
  answer: "Yes. We support roofing contractors, solar installers, main contractors, housing developers, facilities management teams, local authorities, and private homeowners with professional, safety-led scaffolding."
}, {
  id: "restricted-access",
  question: "Can scaffolding be installed where access is restricted?",
  answer: "Yes. Our team is experienced in erecting structures in tight alleyways, over conservatories, across fragile roofs, and on sites with narrow passage limits or restricted footprint boundaries."
}, {
  id: "request-quote",
  question: "How do I request a quote in Fleet?",
  answer: "You can submit project details through our online quote request form or contact our team directly. We review project drawings, site access specifics, and required hire durations before issuing a formal quotation."
}];
export default function FleetFaq() {
  return <section id="fleetfaq" data-nav="light" className="bg-background py-16 md:py-24 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        {/* Header Block */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Badge variant="outline" className="px-3 py-1 font-medium text-xs">
              <HelpCircle className="size-3.5 text-primary mr-1" />
              Fleet Area Questions
            </Badge>
          </div>

          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default mb-4">
            Frequently asked questions about scaffolding in Fleet
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground">
            Clear, factual answers regarding our service areas, specialist access capabilities, and quotation process.
          </p>
        </motion.div>

        {/* Accordion FAQ List */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5,
        delay: 0.1,
        ease: "easeOut"
      }} className="bg-card rounded-xl border border-border p-6 sm:p-8 shadow-sm">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => <AccordionItem key={faq.id} value={faq.id} data-index={i} className="border-b border-border last:border-b-0 py-1">
                <AccordionTrigger className="text-left font-semibold text-foreground hover:text-primary text-base sm:text-lg py-4 transition-[color]">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </motion.div>

        {/* CTA Bar */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.4
      }} transition={{
        duration: 0.5,
        delay: 0.2,
        ease: "easeOut"
      }} className="mt-10 rounded-xl bg-muted/50 border border-border p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-start gap-4 text-left">
            <div className="p-3 rounded-lg bg-primary/10 text-primary shrink-0 mt-0.5">
              <ShieldCheck className="size-6" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-foreground font-default">
                Have specific site requirements in Fleet?
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Our accredited team provides full technical site assessments and transparent proposals.
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto shrink-0">
            <Button asChild size="lg" className="w-full sm:w-auto">
              <Link to="/ContactRequestQuotePage">
                Request a quote
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <Link to="/AreasWeCoverPage">
                View all areas
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}